import {
  AudioOutlined,
  BulbOutlined,
  FileImageOutlined,
  FileOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  SendOutlined,
  StarOutlined,
} from "@ant-design/icons";
import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  useAttachment,
  useAttachmentRuntime,
  useComposer,
  useThreadRuntime,
} from "@assistant-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./Superdoc.module.scss";
import type { AttachmentInfo, WidgetItem } from "./useSuperdocRuntime";
import EhrTransferStatus from "./widgets/EhrTransferStatus";
import FollowUpQuestions from "./widgets/FollowUpQuestions";
import SourcesCitation from "./widgets/SourcesCitation";
import WorkflowConfirm, { type WorkflowConfirmData } from "./widgets/WorkflowConfirm";
import WorkflowPrompt, { type WorkflowPromptData } from "./widgets/WorkflowPrompt";

// ── Types ────────────────────────────────────────────────────────────────

interface InternalMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  widgets?: WidgetItem[];
  attachments?: AttachmentInfo[];
}

interface SuperdocThreadProps {
  runtime: ReturnType<typeof import("@assistant-ui/react").useExternalStoreRuntime>;
  messages: InternalMessage[];
  isRunning: boolean;
  statusLabel?: string | null;
  onSendFollowup: (question: string) => void;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
}

// ── Start Screen ─────────────────────────────────────────────────────────

const startPrompts = [
  {
    label: "Summarize today's notes",
    prompt: "Can you summarize the notes I created today?",
    icon: "document",
  },
  {
    label: "Help with diagnosis",
    prompt: "Help me think through a differential diagnosis for my current patient.",
    icon: "lightbulb",
  },
  {
    label: "Treatment suggestions",
    prompt: "What treatment options should I consider for my patient?",
    icon: "sparkle",
  },
];

const promptIconMap: Record<string, React.ReactNode> = {
  document: <FileTextOutlined style={{ color: "#6366F1", fontSize: 16 }} />,
  lightbulb: <BulbOutlined style={{ color: "#6366F1", fontSize: 16 }} />,
  sparkle: <StarOutlined style={{ color: "#6366F1", fontSize: 16 }} />,
};

const PromptIcon: React.FC<{ type: string }> = ({ type }) => <>{promptIconMap[type] ?? null}</>;

const StartScreen: React.FC = () => {
  const threadRuntime = useThreadRuntime();

  const handlePromptClick = useCallback(
    (prompt: string) => {
      threadRuntime.append({
        role: "user",
        content: [{ type: "text", text: prompt }],
      });
    },
    [threadRuntime],
  );

  return (
    <div className={styles["start-screen__container"]}>
      <div className={styles["start-screen__greeting"]}>
        Hi, I'm Superdoc! <br />
        How can I help you today?
      </div>
      <div className={styles["start-screen__prompts"]}>
        {startPrompts.map((p) => (
          <button
            key={p.label}
            type="button"
            className={styles["start-screen__prompt-button"]}
            onClick={() => handlePromptClick(p.prompt)}
          >
            <PromptIcon type={p.icon} />
            <span className={styles["start-screen__prompt-label"]}>{p.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// ── Assistant Message ────────────────────────────────────────────────────

const AssistantText: React.FC<{ text: string }> = ({ text }: { text: string }) => (
  <div className={styles["msg__assistant-text"]} dangerouslySetInnerHTML={{ __html: formatMarkdown(text) }} />
);

function formatMarkdown(text: string): string {
  if (!text) return "";

  const lines = text.split("\n");
  const output: string[] = [];
  let inUl = false;
  let inOl = false;

  for (let idx = 0; idx < lines.length; idx++) {
    // Escape HTML
    const line = lines[idx].replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    const ulMatch = line.match(/^- (.+)$/);
    const olMatch = line.match(/^(\d+)\. (.+)$/);

    if (ulMatch) {
      if (inOl) {
        output.push("</ol>");
        inOl = false;
      }
      if (!inUl) {
        output.push('<ul style="margin:4px 0;padding-left:20px">');
        inUl = true;
      }
      output.push(`<li>${applyInline(ulMatch[1])}</li>`);
      continue;
    }

    if (olMatch) {
      if (inUl) {
        output.push("</ul>");
        inUl = false;
      }
      if (!inOl) {
        output.push('<ol style="margin:4px 0;padding-left:20px">');
        inOl = true;
      }
      output.push(`<li>${applyInline(olMatch[2])}</li>`);
      continue;
    }

    // Blank line while inside a list: check if a list item follows.
    // If so, keep the list open (just add spacing) to preserve numbering.
    if (line.trim() === "" && (inUl || inOl)) {
      let hasFollowingItem = false;
      for (let j = idx + 1; j < lines.length; j++) {
        const next = lines[j].trim();
        if (next === "") continue; // skip consecutive blank lines
        hasFollowingItem = inUl ? /^- .+$/.test(next) : /^\d+\. .+$/.test(next);
        break;
      }
      if (hasFollowingItem) {
        // Add spacing between list items without closing the list
        output.push('<li style="list-style:none;height:8px"></li>');
        continue;
      }
    }

    // Close any open list
    if (inUl) {
      output.push("</ul>");
      inUl = false;
    }
    if (inOl) {
      output.push("</ol>");
      inOl = false;
    }

    // Empty line = paragraph break
    if (line.trim() === "") {
      output.push("<br/>");
      continue;
    }

    // Headers
    const h3Match = line.match(/^### (.+)$/);
    if (h3Match) {
      output.push(`<h4 style="font-size:14px;font-weight:600;margin:12px 0 4px">${applyInline(h3Match[1])}</h4>`);
      continue;
    }
    const h2Match = line.match(/^## (.+)$/);
    if (h2Match) {
      output.push(`<h3 style="font-size:15px;font-weight:600;margin:12px 0 4px">${applyInline(h2Match[1])}</h3>`);
      continue;
    }
    const h1Match = line.match(/^# (.+)$/);
    if (h1Match) {
      output.push(`<h2 style="font-size:16px;font-weight:600;margin:12px 0 4px">${applyInline(h1Match[1])}</h2>`);
      continue;
    }

    // Horizontal rule
    if (line.trim() === "---") {
      output.push('<hr style="border:none;border-top:1px solid #E5E7EB;margin:12px 0"/>');
      continue;
    }

    // Normal line
    output.push(`${applyInline(line)}<br/>`);
  }

  if (inUl) output.push("</ul>");
  if (inOl) output.push("</ol>");

  // Remove trailing <br/>
  const result = output.join("\n");
  return result.replace(/(<br\/>)+$/, "");
}

function applyInline(text: string): string {
  let s = text;
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*(.+?)\*/g, "<em>$1</em>");
  s = s.replace(
    /`(.+?)`/g,
    '<code style="background:#F3F4F6;padding:1px 4px;border-radius:4px;font-size:12px">$1</code>',
  );
  return s;
}

// ── Widgets Wrapper ──────────────────────────────────────────────────────

const MessageWidgets: React.FC<{
  messageId: string;
  messages: InternalMessage[];
  onSendFollowup: (q: string) => void;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  isRunning: boolean;
}> = ({ messageId, messages, onSendFollowup, onCustomAction, isRunning }) => {
  const msg = messages.find((m) => m.id === messageId);
  if (!msg?.widgets?.length) return null;

  return (
    <>
      {msg.widgets.map((widget) => {
        if (widget.type === "sources") {
          return <SourcesCitation key={widget.id} sources={widget.data as Array<{ title: string; url?: string }>} />;
        }
        if (widget.type === "followup_questions") {
          return (
            <FollowUpQuestions
              key={widget.id}
              questions={widget.data as string[]}
              onSendFollowup={onSendFollowup}
              disabled={isRunning}
            />
          );
        }
        if (widget.type === "ehr_transfer_status") {
          const transferData = widget.data as { status: string; audioId: string };
          return <EhrTransferStatus key={widget.id} status={transferData.status} />;
        }
        if (widget.type === "workflow_prompt") {
          return (
            <WorkflowPrompt
              key={widget.id}
              data={widget.data as WorkflowPromptData}
              onCustomAction={onCustomAction}
              disabled={isRunning}
            />
          );
        }
        if (widget.type === "workflow_confirm") {
          return (
            <WorkflowConfirm
              key={widget.id}
              data={widget.data as WorkflowConfirmData}
              onSendFollowup={onSendFollowup}
              disabled={isRunning}
            />
          );
        }
        return null;
      })}
    </>
  );
};

// ── User Attachment Chip (on sent messages) ──────────────────────────────

const UserAttachmentChip: React.FC<{ attachment: AttachmentInfo }> = ({ attachment }) => (
  <div className={styles["msg__attachment-chip"]}>
    <AttachmentIcon contentType={attachment.contentType} />
    <span className={styles["msg__attachment-chip-name"]}>{attachment.filename}</span>
    {attachment.size != null && (
      <span className={styles["msg__attachment-chip-size"]}>{formatFileSize(attachment.size)}</span>
    )}
  </div>
);

// ── Attachment Preview ────────────────────────────────────────────────────

const AttachmentIcon: React.FC<{ contentType?: string }> = ({ contentType }) =>
  contentType?.startsWith("image/") ? (
    <FileImageOutlined style={{ color: "#6366F1", fontSize: 14 }} />
  ) : (
    <FileOutlined style={{ color: "#6366F1", fontSize: 14 }} />
  );

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ComposerAttachmentPreview: React.FC = () => {
  const attachment = useAttachment();
  const attachmentRuntime = useAttachmentRuntime();
  const handleRemove = useCallback(() => {
    attachmentRuntime.remove();
  }, [attachmentRuntime]);

  // Auto-remove rejected attachments (e.g. file too large)
  useEffect(() => {
    if (attachment.id.startsWith("rejected_")) {
      attachmentRuntime.remove();
    }
  }, [attachment.id, attachmentRuntime]);

  if (attachment.id.startsWith("rejected_")) return null;

  return (
    <div className={styles["composer__attachment-chip"]}>
      <AttachmentIcon contentType={attachment.contentType} />
      <span className={styles["composer__attachment-name"]}>{attachment.name}</span>
      {attachment.file && (
        <span className={styles["composer__attachment-size"]}>{formatFileSize(attachment.file.size)}</span>
      )}
      <button
        type="button"
        className={styles["composer__attachment-remove"]}
        onClick={handleRemove}
        title="Remove attachment"
      >
        ×
      </button>
    </div>
  );
};

// ── Composer ──────────────────────────────────────────────────────────────

const ComposerInputWithPlaceholder: React.FC = () => {
  const text = useComposer((state) => state.text);

  return (
    <div className={styles["composer__input-container"]}>
      {!text && <span className={styles["composer__placeholder"]}>Ask Superdoc anything...</span>}
      <ComposerPrimitive.Input
        placeholder=""
        rows={1}
        maxRows={5}
        autoFocus={false}
        className="superdoc-composer-input"
      />
    </div>
  );
};

const SuperdocComposer: React.FC<{ isRunning?: boolean }> = ({ isRunning }) => (
  <div className={styles.composer__wrapper}>
    <ComposerPrimitive.Root className={styles.composer__root}>
      {/* Pending attachments preview */}
      <ComposerPrimitive.Attachments
        components={{
          Attachment: ComposerAttachmentPreview,
        }}
      />
      <div className={styles["composer__input-row"]}>
        <ComposerPrimitive.AddAttachment className={styles["composer__attach-button"]} title="Attach file">
          <PaperClipOutlined style={{ color: "#6B7280", fontSize: 18 }} />
        </ComposerPrimitive.AddAttachment>
        <ComposerInputWithPlaceholder />
        <ComposerPrimitive.If dictation>
          <ComposerPrimitive.StopDictation className={styles["composer__mic-button-active"]} title="Stop dictation">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="white" xmlns="http://www.w3.org/2000/svg">
              <rect x="0" y="0" width="14" height="14" rx="3" />
            </svg>
          </ComposerPrimitive.StopDictation>
        </ComposerPrimitive.If>
        <ComposerPrimitive.If dictation={false}>
          <ComposerPrimitive.Dictate className={styles["composer__mic-button"]} title="Voice input">
            <AudioOutlined style={{ color: "#6B7280", fontSize: 16 }} />
          </ComposerPrimitive.Dictate>
        </ComposerPrimitive.If>
        {isRunning ? (
          <div className={styles["composer__send-button-loading"]}>
            <div className={styles.composer__spinner} />
          </div>
        ) : (
          <ComposerPrimitive.Send className={styles["composer__send-button"]}>
            <SendOutlined style={{ color: "white", fontSize: 16 }} />
          </ComposerPrimitive.Send>
        )}
      </div>
    </ComposerPrimitive.Root>
    <div className={styles.composer__disclaimer}>Superdoc is experimental. Verify everything.</div>
  </div>
);

// ── Typing Indicator ─────────────────────────────────────────────────────

const TypingIndicator: React.FC<{ label?: string | null }> = ({ label }) => (
  <div className={styles["msg__assistant-container"]}>
    <div className={styles["msg__assistant-bubble"]}>
      <div className={styles.typing__container}>
        <div className={styles.typing__dot} />
        <div className={styles.typing__dot} style={{ animationDelay: "150ms" }} />
        <div className={styles.typing__dot} style={{ animationDelay: "300ms" }} />
        {label && <span className={styles.typing__label}>{label}</span>}
      </div>
    </div>
  </div>
);

// ── History Panel (exported for use by parent) ──────────────────────────

export const HistoryPanel: React.FC<{
  threads: Array<{ id: string; title: string }>;
  currentThreadId: string | null;
  onSwitchThread: (id: string) => void;
  onNewThread: () => void;
  onClose: () => void;
}> = ({ threads, currentThreadId, onSwitchThread, onNewThread, onClose }) => (
  <div className={styles.history__overlay} onClick={onClose}>
    <div className={styles.history__panel} onClick={(e) => e.stopPropagation()}>
      <div className={styles.history__header}>
        <span className={styles["history__header-title"]}>History</span>
        <button type="button" className={styles["history__new-button"]} onClick={onNewThread}>
          + New Chat
        </button>
      </div>
      <div className={styles.history__list}>
        {threads.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`${styles["history__thread-item"]} ${t.id === currentThreadId ? styles["history__thread-item--active"] : ""}`}
            onClick={() => {
              onSwitchThread(t.id);
              onClose();
            }}
          >
            <span className={styles["history__thread-title"]}>{t.title || "Untitled conversation"}</span>
          </button>
        ))}
        {threads.length === 0 && <div className={styles.history__empty}>No previous conversations</div>}
      </div>
    </div>
  </div>
);

// ── Icons (exported for use by parent) ──────────────────────────────────

export const NewChatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#6B7280">
    <path d="M15.6729 3.91275C16.8918 2.6938 18.8682 2.6938 20.0871 3.91275C21.3061 5.1317 21.3061 7.10801 20.0871 8.32696L14.1499 14.2642C13.3849 15.0291 12.3925 15.5254 11.3215 15.6784L9.14142 15.9898C8.82983 16.0343 8.51546 15.9295 8.29289 15.707C8.07033 15.4844 7.96554 15.17 8.01005 14.8584L8.32149 12.6784C8.47449 11.6074 8.97072 10.6149 9.7357 9.84994L15.6729 3.91275ZM18.6729 5.32696C18.235 4.88906 17.525 4.88906 17.0871 5.32696L11.1499 11.2642C10.6909 11.7231 10.3932 12.3186 10.3014 12.9612L10.1785 13.8213L11.0386 13.6985C11.6812 13.6067 12.2767 13.3089 12.7357 12.8499L18.6729 6.91275C19.1108 6.47485 19.1108 5.76486 18.6729 5.32696ZM11 3.99916C11.0004 4.55145 10.5531 4.99951 10.0008 4.99994C9.00227 5.00072 8.29769 5.00815 7.74651 5.06052C7.20685 5.11179 6.88488 5.20104 6.63803 5.32682C6.07354 5.61444 5.6146 6.07339 5.32698 6.63787C5.19279 6.90123 5.10062 7.24891 5.05118 7.85408C5.00078 8.47092 5 9.26324 5 10.3998V13.5998C5 14.7364 5.00078 15.5288 5.05118 16.1456C5.10062 16.7508 5.19279 17.0985 5.32698 17.3618C5.6146 17.9263 6.07354 18.3852 6.63803 18.6729C6.90138 18.807 7.24907 18.8992 7.85424 18.9487C8.47108 18.9991 9.26339 18.9998 10.4 18.9998H13.6C14.7366 18.9998 15.5289 18.9991 16.1458 18.9487C16.7509 18.8992 17.0986 18.807 17.362 18.6729C17.9265 18.3852 18.3854 17.9263 18.673 17.3618C18.7988 17.115 18.8881 16.793 18.9393 16.2533C18.9917 15.7021 18.9991 14.9976 18.9999 13.9991C19.0003 13.4468 19.4484 12.9994 20.0007 12.9998C20.553 13.0003 21.0003 13.4483 20.9999 14.0006C20.9991 14.9788 20.9932 15.7807 20.9304 16.4425C20.8664 17.1159 20.7385 17.7135 20.455 18.2698C19.9757 19.2106 19.2108 19.9755 18.27 20.4549C17.6777 20.7567 17.0375 20.8825 16.3086 20.942C15.6008 20.9999 14.7266 20.9999 13.6428 20.9998H10.3572C9.27339 20.9999 8.39925 20.9999 7.69138 20.942C6.96253 20.8825 6.32234 20.7567 5.73005 20.4549C4.78924 19.9755 4.02433 19.2106 3.54497 18.2698C3.24318 17.6775 3.11737 17.0373 3.05782 16.3085C2.99998 15.6006 2.99999 14.7264 3 13.6426V10.357C2.99999 9.27325 2.99998 8.3991 3.05782 7.69122C3.11737 6.96237 3.24318 6.32218 3.54497 5.72989C4.02433 4.78908 4.78924 4.02418 5.73005 3.54481C6.28633 3.26137 6.88399 3.13346 7.55735 3.06948C8.21919 3.0066 9.02103 3.00071 9.99922 2.99994C10.5515 2.99951 10.9996 3.44688 11 3.99916Z" />
  </svg>
);

export const ExpandIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#6B7280"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 3 21 3 21 9" />
    <polyline points="9 21 3 21 3 15" />
    <line x1="21" y1="3" x2="14" y2="10" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

export const CollapseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#6B7280"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="4 14 10 14 10 20" />
    <polyline points="20 10 14 10 14 4" />
    <line x1="14" y1="10" x2="21" y2="3" />
    <line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);

export const HistoryIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#6B7280">
    <path
      fillRule="evenodd"
      d="M2.954 7.807A1 1 0 0 0 3.968 9c.064.002.13-.003.196-.014l3-.5a1 1 0 0 0-.328-1.972l-.778.13a8 8 0 1 1-2.009 6.247 1 1 0 0 0-1.988.219C2.614 18.11 6.852 22 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2a9.975 9.975 0 0 0-7.434 3.312l-.08-.476a1 1 0 0 0-1.972.328l.44 2.643ZM12 7a1 1 0 0 1 1 1v3.586l2.207 2.207a1 1 0 0 1-1.414 1.414l-2.5-2.5A1 1 0 0 1 11 12V8a1 1 0 0 1 1-1Z"
      clipRule="evenodd"
    />
  </svg>
);

// ── Main Thread Component ────────────────────────────────────────────────

const SuperdocThread: React.FC<SuperdocThreadProps> = ({
  runtime,
  messages,
  isRunning,
  statusLabel,
  onSendFollowup,
  onCustomAction,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const prevMessagesLenRef = useRef(messages.length);

  // Reset scroll lock when a new message is added (user or assistant)
  useEffect(() => {
    if (messages.length !== prevMessagesLenRef.current) {
      prevMessagesLenRef.current = messages.length;
      setUserHasScrolled(false);
    }
  }, [messages.length]);

  // Detect manual scroll away from bottom
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const handleScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      // If user scrolled more than 100px from bottom, consider it intentional
      if (distanceFromBottom > 100) {
        setUserHasScrolled(true);
      } else {
        setUserHasScrolled(false);
      }
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll to bottom only if user hasn't manually scrolled away
  useEffect(() => {
    if (userHasScrolled) return;
    const el = viewportRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages, isRunning, userHasScrolled]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <div className={styles.thread__container}>
        <div className={`${styles.thread__viewport} superdoc-viewport`} ref={viewportRef}>
          {messages.length === 0 && !isRunning ? (
            <StartScreen />
          ) : (
            <div className={styles["thread__message-list"]}>
              {messages.length === 0 && isRunning && <TypingIndicator />}
              {messages.map((msg) => (
                <React.Fragment key={msg.id}>
                  {msg.role === "user" ? (
                    <div className={styles["msg__user-container"]}>
                      <div className={styles["msg__user-bubble"]}>
                        {msg.content}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className={styles["msg__user-attachments"]}>
                            {msg.attachments.map((att) => (
                              <UserAttachmentChip key={att.id} attachment={att} />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      {msg.content && (
                        <div className={styles["msg__assistant-container"]}>
                          <div className={styles["msg__assistant-bubble"]}>
                            <AssistantText text={msg.content} />
                          </div>
                        </div>
                      )}
                      {msg.widgets && msg.widgets.length > 0 && (
                        <div className={styles["msg__widgets-container"]}>
                          <MessageWidgets
                            messageId={msg.id}
                            messages={messages}
                            onSendFollowup={onSendFollowup}
                            onCustomAction={onCustomAction}
                            isRunning={isRunning}
                          />
                        </div>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}
              {/* A live status label (e.g. "Working in your EHR…") keeps an
                  indicator visible even after a completed assistant message —
                  portal workflows narrate in bursts with quiet stretches
                  between them. */}
              {isRunning && statusLabel && <TypingIndicator label={statusLabel} />}
              {isRunning && !statusLabel && messages.length > 0 && messages[messages.length - 1]?.role === "user" && (
                <TypingIndicator />
              )}
              {isRunning &&
                !statusLabel &&
                messages.length > 0 &&
                messages[messages.length - 1]?.role === "assistant" &&
                messages[messages.length - 1]?.content === "" && <TypingIndicator />}
            </div>
          )}
        </div>

        <SuperdocComposer isRunning={isRunning} />
      </div>
    </AssistantRuntimeProvider>
  );
};

export default SuperdocThread;
